'use client';

import { useState } from 'react';
import { Button } from '@/core/components/ui/button';
import { PasswordInput } from '@/core/components/ui/password-input';
import { ShieldCheck } from 'lucide-react';
import { unlockPage } from './actions';
import { toast } from '@/core/utils/toast';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { useRouter } from '@/core/i18n/navigation';

export function PasswordProtectionForm({ slug }: { slug: string }) {
    const { t } = useTranslation();
    const router = useRouter();
    const [password, setPassword] = useState('');
    const [isPending, setIsPending] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!password) return;

        setIsPending(true);
        try {
            const result = await unlockPage(slug, password);
            if (result.success) {
                toast.success(t('page_password.success', 'Strona odblokowana!'));
                // The unlock cookie is httpOnly — re-render on the server, which now fetches the content.
                router.refresh();
            } else if (result.reason === 'rate_limited') {
                toast.error(t('page_password.error_rate_limited', 'Zbyt wiele prób. Spróbuj ponownie za minutę.'));
            } else if (result.reason === 'invalid') {
                toast.error(t('page_password.error_invalid', 'Nieprawidłowe hasło'));
            } else {
                toast.error(t('page_password.error_generic', 'Wystąpił błąd podczas weryfikacji'));
            }
        } catch {
            toast.error(t('page_password.error_generic', 'Wystąpił błąd podczas weryfikacji'));
        } finally {
            setIsPending(false);
        }
    };

    return (
        <div className="max-w-md mx-auto p-8 border rounded-2xl bg-white shadow-xl text-center space-y-6 my-12">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <ShieldCheck className="w-8 h-8 text-amber-600" />
            </div>
            <div>
                <h2 className="text-xl font-bold">{t('page_password.title', 'Wprowadź hasło')}</h2>
                <p className="text-gray-500 mt-2">{t('page_password.subtitle', 'Ta strona jest zabezpieczona. Podaj hasło aby kontynuować.')}</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
                <PasswordInput
                    placeholder={t('page_password.placeholder', 'Twoje hasło...')}
                    className="text-center h-12"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isPending}
                />
                <Button
                    type="submit"
                    className="w-full h-12 bg-gray-900 hover:bg-black text-white rounded-xl transition-all"
                    disabled={isPending}
                >
                    {isPending ? t('page_password.verifying', 'Weryfikacja...') : t('page_password.submit', 'Odblokuj stronę')}
                </Button>
            </form>
        </div>
    );
}
